/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useEffect, Suspense, lazy } from "react";
import { Routes, Route, Navigate, BrowserRouter } from "react-router-dom"; // Updated imports
import { connect, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { ThemeProvider } from "@mui/material/styles";
import routes, { smeBaseName } from "./routes";
// Import custom history if needed for navigation outside of components
import { FormLayout } from "./containers/FormLayout";
import { ServerRequestLoadingScreen } from "./components/ServerRequestLoadingScreen/ServerRequestLoadingScreen";
import {
  ProtectedRoute,
  FinanceTypeProtectedRoute,
  ProspectProtectedRoute,
  OTPGeneratedProtectedRoute,
  OTPProtectedRoute,
  AgentProtectedRoute
} from "./containers/Routers";
import { prospectAutoSave } from "./store/actions/sendProspectToAPI";
import { updateProspect, receiveAppConfig } from "./store/actions/appConfig";
import { theme } from "./theme";
import "./App.scss";
import SessionExpiration from "./containers/Session";
import { ErrorBoundary } from "./containers/Routers/components/ErrorBoundary";
import { financeTypes, financeTypeParamKey, isIslamicBankingParamKey } from "./constants/index";

const ApplicationSubmitted = lazy(() => import("./containers/ApplicationSubmitted"));
const NotFoundPage = lazy(() => import("./containers/NotFoundPage"));
const FinanceProductsComparison = lazy(() => import("./containers/FinanceProductsComparison"));
const DetailedFinance = lazy(() => import("./containers/DetailedFinance"));
const ApplicationOverview = lazy(() => import("./containers/ApplicationOverview"));
const ApplicantInfo = lazy(() => import("./containers/ApplicantInfo"));
const BasicInformation = lazy(() => import("./containers/BasicInformation"));
const FormConfirm = lazy(() => import("./containers/FormConfirm/FormConfirmPage"));
const FormEmailConfirm = lazy(() => import("./containers/FormConfirm/FormEmailConfirmPage"));
const BorrowAmount = lazy(() => import("./containers/BorrowAmount"));
const CompanyInfo = lazy(() => import("./containers/CompanyInfo"));
const CompanyStakeholders = lazy(() => import("./containers/CompanyStakeholders"));
const ComeBackLogin = lazy(() => import("./containers/ComeBackLogin"));
const ComeBackVerification = lazy(() => import("./containers/ComeBackVerification"));
const MyApplications = lazy(() => import("./containers/MyApplications"));
const UploadDocuments = lazy(() => import("./containers/UploadDocuments"));
const SelectServices = lazy(() => import("./containers/SelectServices"));
const SubmitApplication = lazy(() =>
  import("./containers/SelectServices/components/SubmitApplication")
);
const AECBcheck = lazy(() => import("./containers/SelectServices/components/AECBChecks"));
const FTScheck = lazy(() => import("./containers/SelectServices/components/FTSConsentCheck"));
const FinalQuestions = lazy(() => import("./containers/FinalQuestions"));
const ConsentVerify = lazy(() => import("./containers/ConsentVerify"));
const ConsentVerifyPostGenerate = lazy(() =>
  import("./containers/ConsentVerify/ConsentVerifyPostGenerate")
);
const Verify = lazy(() => import("./containers/ConsentVerify/components/Verify"));
const ConsentAuthorized = lazy(() =>
  import("./containers/ConsentVerify/components/ConsentAuthorized")
);
const UploadAdditionalDocuments = lazy(() => import("./containers/UploadAdditionalDocuments"));
const UploadAdditionalDocumentsVerify = lazy(() =>
  import("./containers/UploadAdditionalDocuments/components/AdditionalDocumentsVerify")
);
const AdditionalDocumentsSubmitted = lazy(() =>
  import("./containers/UploadAdditionalDocuments/components/AdditionalDocumentsSubmitted")
);
const UploadAdditionalDocumentsVerifyGenerated = lazy(() =>
  import(
    "./containers/UploadAdditionalDocuments/components/AdditionalDocumentsVerify/AdditionalDocumentsVerifyGenerated"
  )
);

const ApplicationVerification = lazy(() => import("./containers/ApplicationVerification"));
const CompanyInfoPE = lazy(() => import("./containers/PostEligibilityCompanyInfo"));
const CompanyStakeholdersPE = lazy(() => import("./containers/PostEligibilityCompanyStakeholders"));
const UploadDocumentsPE = lazy(() => import("./containers/PostEligibilityUploadDocuments"));

const ExistingCustomer = lazy(() => import("./containers/ExistingCustomer"));
const AdditionalDocumentSignatories = lazy(() =>
  import("./containers/AdditionalDocumentSignatories")
);

const UserVerify = lazy(() => import("./containers/UserVerify"));
const UserAuthorize = lazy(() => import("./containers/UserVerify/components/UserAuthorize"));
const UserAuthorized = lazy(() => import("./containers/UserVerify/components/UserAuthorized"));
const userVerifyGenerated = lazy(() => import("./containers/UserVerify/UserVerifyGenerated"));
const AgentLogin = lazy(() =>
  import("./containers/AgentPages").then(module => ({ default: module.Login }))
);
const AgentSearchProspect = lazy(() =>
  import("./containers/AgentPages").then(module => ({ default: module.Admin }))
);
const AgentSearchedAppInfo = lazy(() =>
  import("./containers/AgentPages").then(module => ({ default: module.SearchedAppInfo }))
);

const App = ({ receiveAppConfig, prospectAutoSave }) => {
  // Track if history is ready to be used
  // const [isHistoryReady, setIsHistoryReady] = useState(false);

  useEffect(() => {
    receiveAppConfig();
    prospectAutoSave();

    // Ensure history is initialized - this needs to be handled differently in React Router v6
    // setIsHistoryReady(true);
  }, [receiveAppConfig, prospectAutoSave]);

  const dispatch = useDispatch();
  let url_string = window.location.href;
  let url = new URL(url_string);
  const financeTypeParamValue = url.searchParams.get(financeTypeParamKey);
  const isIslamicBanking = url.searchParams.get(isIslamicBankingParamKey);

  // Find the finance type value using Array.prototype.find for clarity and performance
  const financeTypeObj = financeTypes.find(item => item.param === financeTypeParamValue);
  const financeTypeValue = financeTypeObj ? financeTypeObj.value : undefined;

  useEffect(() => {
    // In React Router v6, programmatic navigation is handled differently
    // Only proceed with the navigation logic if parameters are available
    try {
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;

      if (
        financeTypeParamValue &&
        financeTypeValue !== undefined &&
        (currentPath === routes.applicantInfo || currentPath === routes.applicantInfo + "/")
      ) {
        dispatch(
          updateProspect({
            "prospect.applicationInfo.financeType": financeTypeValue,
            "prospect.applicationInfo.islamicBanking": isIslamicBanking === "true"
          })
        );
        // Only redirect if not already at the correct URL
        const targetUrl = routes.applicantInfo + currentSearch;
        if (currentPath + currentSearch !== targetUrl) {
          window.location.href = targetUrl;
        }
      } else if (
        financeTypeValue === undefined &&
        (currentPath === routes.applicantInfo || currentPath === routes.applicantInfo + "/")
      ) {
        window.location.href = routes.financeProductComparison;
      }
    } catch (error) {
      console.error("Error in navigation:", error);
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        {/* Replace ConnectedRouter with BrowserRouter */}
        <BrowserRouter>
          <FormLayout>
            <Suspense fallback={ServerRequestLoadingScreen()}>
              <Routes>
                <Route path={routes.login} element={<AgentLogin />} />
                <Route
                  path={routes.searchProspect}
                  element={<AgentProtectedRoute component={AgentSearchProspect} />}
                />
                <Route
                  path={routes.SearchedAppInfo}
                  element={<AgentProtectedRoute component={AgentSearchedAppInfo} />}
                />

                <Route path="*" element={<Navigate to={routes.searchProspect} replace />} />

                <Route
                  path={routes.comeBackLogin}
                  element={<ProtectedRoute component={ComeBackLogin} />}
                />

                <Route
                  path={routes.comeBackLoginVerification}
                  element={<OTPGeneratedProtectedRoute component={ComeBackVerification} />}
                />
                <Route
                  path={routes.applicationVerification}
                  element={<OTPGeneratedProtectedRoute component={ApplicationVerification} />}
                />
                <Route
                  path={routes.MyApplications}
                  element={<ProtectedRoute component={MyApplications} />}
                />
                <Route
                  path={routes.aecbFtsAuthorize}
                  element={<OTPProtectedRoute component={Verify} />}
                />
                <Route
                  path={routes.aecbFtsAuthorizeGenerated}
                  element={<ProtectedRoute component={ConsentVerifyPostGenerate} />}
                />
                <Route
                  path={routes.consentVerify}
                  element={<ProtectedRoute component={ConsentVerify} />}
                />
                <Route
                  path={routes.consentAuthorized}
                  element={<OTPProtectedRoute component={ConsentAuthorized} />}
                />
                <Route
                  path={routes.uploadAdditionalDocumentsVerify}
                  element={<ProtectedRoute component={UploadAdditionalDocumentsVerify} />}
                />
                <Route
                  path={routes.uploadAdditionalDocumentsVerifyGenerated}
                  element={<ProtectedRoute component={UploadAdditionalDocumentsVerifyGenerated} />}
                />
                <Route
                  path={routes.additionalDocuments}
                  element={<OTPProtectedRoute component={UploadAdditionalDocuments} />}
                />
                <Route
                  path={routes.additionalDocumentsSubmitted}
                  element={<OTPProtectedRoute component={AdditionalDocumentsSubmitted} />}
                />
                <Route
                  path={routes.financeProductComparison}
                  element={<ProtectedRoute component={FinanceProductsComparison} />}
                />
                <Route
                  path={routes.ApplicationSubmitted}
                  element={<ProspectProtectedRoute component={ApplicationSubmitted} />}
                />
                <Route
                  path={routes.applicantInfo}
                  element={<ProtectedRoute component={ApplicantInfo} />}
                />
                <Route
                  path={routes.basicInformation}
                  element={<ProspectProtectedRoute component={BasicInformation} />}
                />
                <Route
                  path={routes.borrowAmount}
                  element={<ProspectProtectedRoute component={BorrowAmount} />}
                />
                <Route
                  path={routes.verifyOtp}
                  element={<ProspectProtectedRoute component={FormConfirm} />}
                />
                <Route
                  path={routes.verifyEmailOtp}
                  element={<ProspectProtectedRoute component={FormEmailConfirm} />}
                />
                <Route
                  path={routes.companyInfo}
                  element={<ProspectProtectedRoute component={CompanyInfo} />}
                />
                <Route
                  path={routes.finalQuestions}
                  element={<ProspectProtectedRoute component={FinalQuestions} />}
                />
                <Route
                  path={routes.stakeholdersInfo}
                  element={<ProspectProtectedRoute component={CompanyStakeholders} />}
                />
                <Route
                  path={routes.uploadDocuments}
                  element={<ProspectProtectedRoute component={UploadDocuments} />}
                />
                <Route
                  path={routes.selectServices}
                  element={<ProspectProtectedRoute component={SelectServices} />}
                />
                <Route
                  path={routes.SubmitApplication}
                  element={<ProspectProtectedRoute component={SubmitApplication} />}
                />
                <Route
                  path={routes.userVerify}
                  element={<ProtectedRoute component={UserVerify} />}
                />
                <Route
                  path={routes.userVerifyGenerated}
                  element={<ProtectedRoute component={userVerifyGenerated} />}
                />
                <Route
                  path={routes.userAuthorize}
                  element={<OTPProtectedRoute component={UserAuthorize} />}
                />
                <Route
                  path={routes.userAuthorized}
                  element={<OTPProtectedRoute component={UserAuthorized} />}
                />
                <Route
                  path={routes.AECBcheck}
                  element={<ProspectProtectedRoute component={AECBcheck} />}
                />
                <Route
                  path={routes.FTScheck}
                  element={<ProspectProtectedRoute component={FTScheck} />}
                />
                <Route
                  path={routes.companyInfoPE}
                  element={<ProspectProtectedRoute component={CompanyInfoPE} />}
                />
                <Route
                  path={routes.stakeholdersInfoPE}
                  element={<ProspectProtectedRoute component={CompanyStakeholdersPE} />}
                />
                <Route
                  path={routes.uploadDocumentsPE}
                  element={<ProspectProtectedRoute component={UploadDocumentsPE} />}
                />
                <Route
                  path={routes.existingCustomer}
                  element={<ProspectProtectedRoute component={ExistingCustomer} />}
                />
                <Route
                  path={routes.SalesInitiate}
                  element={<ProspectProtectedRoute component={ExistingCustomer} />}
                />
                <Route
                  path={routes.additionalDocumentSignatories}
                  element={<ProspectProtectedRoute component={AdditionalDocumentSignatories} />}
                />
                <Route
                  path={routes.detailedFinance}
                  element={<FinanceTypeProtectedRoute component={DetailedFinance} />}
                />
                <Route
                  path={routes.applicationOverview}
                  element={<FinanceTypeProtectedRoute component={ApplicationOverview} />}
                />

                <Route path="/" element={<Navigate to={routes.financeProductComparison} />} />
                <Route
                  path={`(${smeBaseName})?`}
                  element={<Navigate to={routes.financeProductComparison} />}
                />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              <SessionExpiration />
            </Suspense>
          </FormLayout>
        </BrowserRouter>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

const mapDispatchToProps = {
  receiveAppConfig,
  prospectAutoSave
};
App.propTypes = {
  receiveAppConfig: PropTypes.func.isRequired,
  prospectAutoSave: PropTypes.func.isRequired
};

export default connect(
  null,
  mapDispatchToProps
)(App);
